import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ethers } from 'ethers';
import { provider, ethersUtils, createContract, getAccounts, initializeEthers } from './EthersSetup';
import TicTacToe from '../deployedContractsInfo/TicTacToe.json';
import GameMakerConfig from '../deployedContractsInfo/GameMaker.json';
import contractAddresses from '../deployedContractsInfo/contract-address.json';
import { AddressDisplayLong } from './AddressDisplay.js';
import { TransactionLink } from './LinkDisplay';
import blockchainDataManager from '../services/BlockchainDataManager.js';
import TopRightBar from './basics/TopRightBar';
import GameTransactionHistory from './basics/GameTransactionHistory';
import { FullGameSkeleton, GameBoardSkeleton, TransactionPanelSkeleton } from './ui/SkeletonLoader';

const TicTacToeGame = () => {
  const { gameID = '1' } = useParams();
  const [gameState, setGameState] = useState({
    player1: '',
    player2: '',
    currentPlayer: '',
    status: '',
    gameFinished: false,
    lastMoveTime: 0,
    contribution1: 0,
    contribution2: 0,
    board: Array(9).fill(null),
    winner: ''
  });

  const [userState, setUserState] = useState({ userAddress: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [uiReady, setUiReady] = useState(false);
  const [loadingStages, setLoadingStages] = useState({
    gameBasics: true,    // Player names, status
    gameDetails: true,   // Board state, moves
    userFeatures: true   // Wallet-dependent features
  });
  const [rulesCollapsed, setRulesCollapsed] = useState(true);
  const [isWalletInitialized, setIsWalletInitialized] = useState(false);
  const [transactionPending, setTransactionPending] = useState(null); // { position: number, message: string }

  // Memoized game events for GameTransactionHistory to prevent constant reloading
  const ticTacToeGameEvents = useMemo(() => [
    { name: 'MoveMade', label: 'Move Made' }
  ], []);

  // Ref to store current game state for event handlers (prevents stale closures)
  const gameStateRef = useRef(gameState);

  // Contract state management for async creation
  const [readOnlyContract, setReadOnlyContract] = useState(null);

  // Initialize read-only contract asynchronously
  useEffect(() => {
    const initReadOnlyContract = async () => {
      try {
        const contract = await createContract(contractAddresses.TicTacToe, TicTacToe.abi, false);
        setReadOnlyContract(contract);
      } catch (error) {
        console.warn('[TicTacToe] Failed to create read-only contract:', error);
        setReadOnlyContract(null);
      }
    };
    initReadOnlyContract();
  }, []);

  // Signer-dependent contract (created when wallet is available)
  const [signerContract, setSignerContract] = useState(null);

  // Staggered loading functions for different data stages
  const loadGameBasics = useCallback(async () => {
    try {
      const contractToUse = readOnlyContract || await createContract(contractAddresses.TicTacToe, TicTacToe.abi, false);
      if (!contractToUse) {
        throw new Error('No contract available');
      }

      const game = await contractToUse.games(gameID);
      const basicGameState = {
        player1: game.player1,
        player2: game.player2,
        status: game.status.toString(),
        gameFinished: ['2', '3', '4', '5'].includes(game.status.toString()),
        lastMoveTime: Number(game.lastMoveTime),
        contribution1: game.contribution1,
        contribution2: game.contribution2
      };

      setGameState(prev => ({ ...prev, ...basicGameState }));
      setLoadingStages(prev => ({ ...prev, gameBasics: false }));
      console.log('[TicTacToe] Game basics loaded');
    } catch (error) {
      console.error('[TicTacToe] Failed to load game basics:', error);
      setErrorMessage('Failed to load game information');
      setLoadingStages(prev => ({ ...prev, gameBasics: false }));
    }
  }, [gameID, readOnlyContract]);

  const loadGameDetails = useCallback(async () => {
    try {
      const contractToUse = readOnlyContract || await createContract(contractAddresses.TicTacToe, TicTacToe.abi, false);
      if (!contractToUse) {
        throw new Error('No contract available');
      }

      const board = await contractToUse.getBoard(gameID);
      console.log(`[TicTacToe] Board loaded:`, board);

      // Determine winner
      let winner = '';
      const status = gameState.status;
      if (status === '3') winner = gameState.player1;
      else if (status === '4') winner = gameState.player2;
      else if (status === '2') winner = 'draw';

      setGameState(prev => ({ ...prev, board, winner }));
      setLoadingStages(prev => ({ ...prev, gameDetails: false }));
      console.log('[TicTacToe] Game details loaded');
    } catch (error) {
      console.error('[TicTacToe] Failed to load game details:', error);
      setLoadingStages(prev => ({ ...prev, gameDetails: false }));
    }
  }, [gameID, readOnlyContract, gameState.status, gameState.player1, gameState.player2]);


  const initializeWalletFeatures = useCallback(async () => {
    try {
      if (!userState.userAddress) {
        setLoadingStages(prev => ({ ...prev, userFeatures: false }));
        return;
      }

      // Create signer contract when wallet is available
      const signerContractInstance = await createContract(contractAddresses.TicTacToe, TicTacToe.abi, true);
      setSignerContract(signerContractInstance);

      setLoadingStages(prev => ({ ...prev, userFeatures: false }));
      console.log('[TicTacToe] Wallet features initialized');
    } catch (error) {
      console.error('[TicTacToe] Failed to initialize wallet features:', error);
      setLoadingStages(prev => ({ ...prev, userFeatures: false }));
    }
  }, [userState.userAddress]);

  // Move loadGameData outside useEffect so it can be used by manual refresh button
  const loadGameData = async (isInitialLoad = false, forceRefresh = false, contractInstance = null) => {
    try {
      // Use provided contract instance or stored state, only create fresh if necessary
      let ticTacToeContractToUse = contractInstance || ticTacToeContract;

      if (!ticTacToeContractToUse) {
        console.log('[TicTacToe] No contract available, creating fresh instance');
        ticTacToeContractToUse = await createContract(contractAddresses.TicTacToe, TicTacToe.abi);
      }

      if (forceRefresh) {
        console.log(`[TicTacToe] Force refresh - using ${contractInstance ? 'provided' : 'stored'} contract`);
      }
        
        const game = await ticTacToeContractToUse.games(gameID);
        const player1 = game.player1;
        const player2 = game.player2;
        const status = game.status.toString();

        const currentPlayer = (status === '0' || status === '1') ? (status === '0' ? player1 : player2) : '';
        const gameFinished = (status === '2' || status === '3' || status === '4' || status === '5');
        const lastMoveTime = Number(game.lastMoveTime);
        const contribution1 = game.contribution1;
        const contribution2 = game.contribution2;
        
        console.log(`[TicTacToe Debug] About to call getBoard(${gameID})`);
        let board;
        try {
          board = await ticTacToeContractToUse.getBoard(gameID);
          console.log(`[TicTacToe Debug] getBoard() succeeded, result:`, board);
        } catch (boardError) {
          console.error(`[TicTacToe Debug] getBoard() failed:`, boardError);
          throw boardError;
        }
        
        console.log(`[TicTacToe] Game data loaded - Status: ${status}, Block: ${await provider.getBlockNumber()}`);
        console.log(`[TicTacToe] Board state - Occupied squares:`, board.filter(addr => addr !== '0x0000000000000000000000000000000000000000').length);

        let winner = '';
        if (status === '3') {
          winner = player1;
        } else if (status === '4') {
          winner = player2;
        } else if (status === '2') {
          winner = 'draw';
        }

        const newGameState = { 
          player1, 
          player2, 
          currentPlayer, 
          status, 
          gameFinished, 
          lastMoveTime, 
          contribution1, 
          contribution2, 
          board,
          winner
        };

        // Only update state if something actually changed (or force refresh)
        setGameState(prevState => {
          const hasChanged = !prevState.player1 ||
            prevState.status !== status ||
            prevState.currentPlayer !== currentPlayer ||
            prevState.lastMoveTime !== lastMoveTime ||
            JSON.stringify(prevState.board) !== JSON.stringify(board);
          
          console.log(`[TicTacToe] State update - hasChanged: ${hasChanged}, forceRefresh: ${forceRefresh}`);
          
          if (hasChanged || forceRefresh) {
            console.log('Game state changed, updating...');
            return newGameState;
          } else {
            console.log('No game state changes detected');
            return prevState;
          }
        });

        
        // Loading stages are managed by individual functions now
      } catch (error) {
        if (error.message.includes("No wallet connected")) {
          console.log('TicTacToe: Wallet not connected yet, skipping game data load');
          setErrorMessage('Connecting to wallet...');
        } else if (error.message.includes("Game with ID doesn't exist")) {
          setErrorMessage("Game with ID doesn't exist");
        } else {
          console.error('Failed to load game data:', error);
          setErrorMessage('Failed to load game data');
        }
        // Loading stages are managed by individual functions now
      }
    };

  // Wallet handlers for TopRightBar
  const handleSwitchWallet = () => {
    // For individual game pages, we'll navigate back to main page for wallet switching
    window.location.href = '/';
  };

  const handleWalletDisconnect = async () => {
    try {
      const accounts = await getAccounts();
      setUserState({ userAddress: accounts[0] || '' });
    } catch (error) {
      console.error('Error after wallet disconnect:', error);
      window.location.href = '/';
    }
  };


  // Create stable contract instances
  const [ticTacToeContract, setTicTacToeContract] = useState(null);
  const [ethersContract, setEthersContract] = useState(null);
  const [gameMakerContract, setGameMakerContract] = useState(null);

  // Create GameMaker contract for transaction history
  useEffect(() => {
    const initGameMakerContract = async () => {
      try {
        const contract = await createContract(contractAddresses.GameMaker, GameMakerConfig.abi);
        setGameMakerContract(contract);
      } catch (error) {
        console.error('TicTacToe: Failed to create GameMaker contract:', error);
      }
    };
    initGameMakerContract();
  }, []);

  // Main initialization with staggered loading
  useEffect(() => {
    // Show UI immediately
    setUiReady(true);

    // Initialize wallet in background, then load game basics
    const initWallet = async () => {
      try {
        await initializeEthers();
        const accounts = await getAccounts();
        if (accounts.length > 0) {
          setUserState({ userAddress: accounts[0] });
        }
        setIsWalletInitialized(true);

        // Now that provider is ready, load game basics
        loadGameBasics();
      } catch (error) {
        console.error('TicTacToe: Failed to initialize wallet:', error);
        setIsWalletInitialized(true); // Still allow component to continue

        // Try to load game basics even if wallet failed (read-only mode)
        loadGameBasics();
      }
    };
    initWallet();
  }, [loadGameBasics]);

  // Load game details after basics are loaded
  useEffect(() => {
    if (!loadingStages.gameBasics && gameState.player1) {
      loadGameDetails();
    }
  }, [loadingStages.gameBasics, gameState.player1, loadGameDetails]);

  // Initialize wallet features when wallet is ready
  useEffect(() => {
    if (isWalletInitialized) {
      initializeWalletFeatures();
    }
  }, [isWalletInitialized, initializeWalletFeatures]);


  // Set browser tab title for the game
  useEffect(() => {
    const originalTitle = document.title;
    document.title = `TicTacToe #${gameID}`;
    
    // Cleanup: restore original title when component unmounts
    return () => {
      document.title = originalTitle;
    };
  }, [gameID]);

  // Initialize contract and load initial data
  useEffect(() => {
    if (!isWalletInitialized) return;

    const initializeContract = async () => {
      try {
        const contract = await createContract(contractAddresses.TicTacToe, TicTacToe.abi);
        setTicTacToeContract(contract);

        // Create ethers contract for events using the provider from EthersSetup
        if (provider) {
          const ethersContract = new ethers.Contract(contractAddresses.TicTacToe, TicTacToe.abi, provider);
          setEthersContract(ethersContract);
          console.log('🔧 TicTacToe: Ethers contract created for events');
        } else {
          console.log('🔧 TicTacToe: No provider available for ethers contract');
        }

        await loadGameData(true, false, contract); // Initial load with loading state, pass fresh contract
        // Clear any previous wallet connection error
        if (errorMessage === 'Connecting to wallet...') {
          setErrorMessage('');
        }
      } catch (error) {
        console.error('Error creating TicTacToe contract:', error);
        setTicTacToeContract(null);
        setEthersContract(null);
        // Don't call loadGameData if contract creation fails
      }
    };

    initializeContract();
  }, [gameID, createContract, isWalletInitialized]);

  // Keep gameStateRef updated for event handlers
  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  // Auto-pause polling when game finishes
  useEffect(() => {
    const isGameFinished = gameState.gameFinished === true;

    // Dispatch auto-pause event based on game state
    window.dispatchEvent(new CustomEvent('setPollingPause', {
      detail: {
        isPaused: isGameFinished,
        reason: isGameFinished ? 'game-finished' : ''
      }
    }));
  }, [gameState.gameFinished]);

  // Event handler for game state updates
  const handleGameUpdate = useCallback(async () => {
    console.log('[TicTacToe] Event detected, refreshing game state...');

    // Run data loading and transaction info in parallel for better performance
    const currentGameState = gameStateRef.current;
    const promises = [
      loadGameData(false, false) // Refresh without loading state
    ];


    await Promise.all(promises);
  }, [gameID]); // Only gameID dependency - no more stale closure issues

  // Set up polling system for TicTacToe page (2 contracts: GameMaker + TicTacToe)
  useEffect(() => {
    if (!provider || !ethersContract) return;

    // Configure polling for this game page - ensure full setup
    const setupPolling = async () => {
      try {
        // Initialize the complete polling system
        await blockchainDataManager.setupSmartPolling();

        // Add TicTacToe to the polled contracts (GameMaker is already included)
        blockchainDataManager.addToPolledContracts('TicTacToe');

        console.log('[TicTacToe] Polling system fully initialized with 2 contracts');
      } catch (error) {
        console.error('[TicTacToe] Failed to setup polling system:', error);
      }
    };

    setupPolling();

    // Subscribe to game updates from polling system
    const handleGameInstanceUpdate = (data) => {
      // Filter updates for this specific game
      if (data.source === 'polling' && (data.contract === 'TicTacToe' || data.contract === 'GameMaker')) {
        console.log(`[TicTacToe] Polling update from ${data.contract}, refreshing game state...`);
        handleGameUpdate();
      }
    };

    const unsubscribe = blockchainDataManager.subscribe('gameInstancesUpdated', handleGameInstanceUpdate);

    return unsubscribe;
  }, [provider, ethersContract, gameID, handleGameUpdate]);

  // Monitor contract state changes for debugging
  useEffect(() => {
    console.log('📋 TicTacToe: Contract state changed:', {
      hasMainContract: !!ticTacToeContract,
      hasEthersContract: !!ethersContract
    });
  }, [ticTacToeContract, ethersContract]);

  // Log polling system status for debugging
  useEffect(() => {
    console.log(`[TicTacToe] Polling system configured for contracts: GameMaker, TicTacToe`);
  }, [gameID]);

  const _makeMove = async (index) => {
    if (!userState.userAddress) {
      setErrorMessage('User address not available. Have you connected your wallet?');
      return;
    }

    if (gameState.gameFinished) {
      setErrorMessage('Game is already finished');
      return;
    }

    if (gameState.board[index] !== '0x0000000000000000000000000000000000000000') {
      setErrorMessage('This square is already taken');
      return;
    }

    if (gameState.currentPlayer.toLowerCase() !== userState.userAddress.toLowerCase()) {
      setErrorMessage('It\'s not your turn');
      return;
    }

    // Set transaction pending state
    const currentPlayerSymbol = gameState.status === '0' ? 'X' : 'O';
    setTransactionPending({ 
      position: index,
      message: `Placing ${currentPlayerSymbol} on square ${index + 1}...`
    });

    try {
      // Use pre-created signer contract or create if not available
      const ticTacToeContract = signerContract || await createContract(contractAddresses.TicTacToe, TicTacToe.abi, true);
      console.log('Contract created with signer:', !!ticTacToeContract.signer);
      console.log('Contract signer address check...');
      try {
        const signerAddress = await ticTacToeContract.signer.getAddress();
        console.log('Actual signer address:', signerAddress);
      } catch (e) {
        console.log('Failed to get signer address:', e.message);
      }
      const estimatedGas = await ticTacToeContract.estimateGas.interact(gameID, index + 1);
      const gasLimit = Math.floor(Number(estimatedGas) * 1.2);
      const gasPrice = await provider.getGasPrice();
      const gasPriceBid = Math.floor(Number(gasPrice) * 1.2);
      // Nonce handled automatically by Ethers.js signer

      const tx = await ticTacToeContract.interact(gameID, index + 1, {
        gasLimit: gasLimit,
        gasPrice: gasPriceBid
      });
      await tx.wait(); // Wait for confirmation
      console.log('Move transaction sent:', tx.transactionHash);
      setErrorMessage('');
      
      // Wait for transaction confirmation and then refresh game state
      await tx.wait?.() || new Promise(resolve => setTimeout(resolve, 2000));
      console.log('Transaction confirmed, refreshing game data...');
      
      // Manually refresh game data for immediate feedback
      const ticTacToeContractForRefresh = await createContract(contractAddresses.TicTacToe, TicTacToe.abi);
      const game = await ticTacToeContractForRefresh.games(gameID);
      const board = await ticTacToeContractForRefresh.getBoard(gameID);
      
      setGameState(prevState => ({
        ...prevState,
        status: game.status.toString(),
        currentPlayer: (game.status.toString() === '0' || game.status.toString() === '1') ? 
          (game.status.toString() === '0' ? game.player1 : game.player2) : '',
        gameFinished: (game.status.toString() === '2' || game.status.toString() === '3' || 
                      game.status.toString() === '4' || game.status.toString() === '5'),
        lastMoveTime: Number(game.lastMoveTime),
        board: board,
        winner: game.status.toString() === '3' ? game.player1 : 
                game.status.toString() === '4' ? game.player2 : 
                game.status.toString() === '2' ? 'draw' : ''
      }));
      
    } catch (error) {
      setErrorMessage(error.message || "An unexpected error occurred");
    } finally {
      // Clear transaction pending state
      setTransactionPending(null);
    }
  };

  const formatEth = (wei) => {
    return ethersUtils.fromWei(wei.toString(), 'ether');
  };

  const getUserRole = () => {
    const isSamePlayer = gameState.player1.toLowerCase() === gameState.player2.toLowerCase();
    const isPlayer1 = userState.userAddress.toLowerCase() === gameState.player1.toLowerCase();
    const isPlayer2 = userState.userAddress.toLowerCase() === gameState.player2.toLowerCase();

    if (isSamePlayer && (isPlayer1 || isPlayer2)) {
      return 'Both (X & O)';
    } else if (isPlayer1) {
      return 'Player 1 (X)';
    } else if (isPlayer2) {
      return 'Player 2 (O)';
    }
    return 'Spectator';
  };

  const getStatusMessage = () => {
    if (gameState.gameFinished) {
      if (gameState.status === '2') {
        return 'Game ended in a draw';
      } else if (gameState.status === '3') {
        return 'Player 1 (X) wins!';
      } else if (gameState.status === '4') {
        return 'Player 2 (O) wins!';
      } else if (gameState.status === '5') {
        return 'Game ended - Killed';
      }
    } else {
      const currentPlayerSymbol = gameState.status === '0' ? 'X' : gameState.status === '1' ? 'O' : 'Unknown';
      return `${currentPlayerSymbol}'s turn`;
    }
    return 'Loading...';
  };

  const getWinnerMessage = () => {
    if (!gameState.gameFinished || !userState.userAddress) return '';
    
    const isPlayer1 = userState.userAddress.toLowerCase() === gameState.player1.toLowerCase();
    const isPlayer2 = userState.userAddress.toLowerCase() === gameState.player2.toLowerCase();
    const isPlayer = isPlayer1 || isPlayer2;
    
    // Only show personalized messages for actual players, not spectators
    if (!isPlayer) return '';
    
    if (gameState.winner === 'draw') {
      return 'You drawed!';
    } else if (gameState.winner === gameState.player1) {
      return isPlayer1 ? 'You won!' : 'You lost!';
    } else if (gameState.winner === gameState.player2) {
      return isPlayer2 ? 'You won!' : 'You lost!';
    }
    return '';
  };

  const renderBoard = () => {
    // Show skeleton while loading game details
    if (loadingStages.gameDetails) {
      return <GameBoardSkeleton gridSize={3} />;
    }

    return (
      <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto mb-6">
        {gameState.board.map((square, index) => {
          const isPlayer1 = square === gameState.player1;
          const isPlayer2 = square === gameState.player2;
          const isEmpty = square === '0x0000000000000000000000000000000000000000';
          const isGameNotFinished = !gameState.gameFinished;
          const isSquareEmpty = isEmpty;
          const isUserTurn = gameState.currentPlayer.toLowerCase() === userState.userAddress.toLowerCase();
          const noTransaction = !transactionPending;
          const userFeaturesReady = !loadingStages.userFeatures;

          const canPlay = isGameNotFinished && isSquareEmpty && isUserTurn && noTransaction && userFeaturesReady;

          const isCurrentPlayer = gameState.currentPlayer.toLowerCase() === userState.userAddress.toLowerCase();
          const currentPlayerSymbol = gameState.status === '0' ? 'X' : 'O';

          return (
            <button
              key={index}
              onClick={() => _makeMove(index)}
              className={`
                w-20 h-20 border-2 border-gray-300 rounded-lg text-3xl font-bold
                flex items-center justify-center transition-all duration-200 bg-blue-100 text-gray-800 relative
                ${canPlay ? 'hover:bg-blue-300 cursor-pointer group' : 'cursor-not-allowed'}
                ${isEmpty && !isCurrentPlayer ? 'hover:bg-blue-100' : ''}
                ${transactionPending && transactionPending.position === index ? 'bg-yellow-100 border-yellow-400' : ''}
                ${loadingStages.userFeatures ? 'opacity-50' : ''}
              `}
              disabled={!canPlay}
              title={canPlay && isEmpty ? `Place ${currentPlayerSymbol}` : ''}
            >
              {/* Show pending indicator for the square being processed */}
              {transactionPending && transactionPending.position === index ? (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
              ) : (
                isPlayer1 ? 'X' : isPlayer2 ? 'O' :
                (canPlay && isEmpty ?
                  <span className="opacity-0 group-hover:opacity-50 transition-opacity duration-200">{currentPlayerSymbol}</span> :
                  '')
              )}
            </button>
          );
        })}
      </div>
    );
  };

  // Show full skeleton while UI is not ready
  if (!uiReady) {
    return <FullGameSkeleton gameType="tictactoe" />;
  }

  if (errorMessage && !gameState.player1) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          Error: {errorMessage}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <style>
        {`
          .collapsible-button {
            background: white !important;
            background-color: white !important;
            background-image: none !important;
            color: #374151 !important;
            border: none !important;
            padding: 0 !important;
            border-radius: 0 !important;
          }
          .collapsible-container {
            background-color: white !important;
            border-radius: 0.5rem !important;
            padding: 1.5rem !important;
            margin-bottom: 1.5rem !important;
            border: none !important;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
          }
          
          /* Shared game message styling */
          .game-message-main {
            font-size: 1.5rem;
            font-weight: 700;
            color: #10b981;
            text-align: center;
            margin-bottom: 0.5rem;
          }
          .game-message-sub {
            font-size: 1rem;
            font-weight: 400;
            color: #374151;
            text-align: center;
          }
          .game-message-pending {
            font-size: 1rem;
            font-weight: 500;
            color: #f59e0b;
            text-align: center;
          }
          .game-message-waiting {
            font-size: 1.5rem;
            font-weight: 700;
            color: #f59e0b;
            text-align: center;
            margin-bottom: 0.5rem;
          }
          
          /* Win/Loss Animations */
          @keyframes celebrate {
            0%, 100% { 
              transform: scale(1);
              box-shadow: 0 0 10px rgba(16, 185, 129, 0.3);
            }
            50% { 
              transform: scale(1.02);
              box-shadow: 0 0 15px rgba(16, 185, 129, 0.5);
            }
          }
          
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-2px); }
            50% { transform: translateX(2px); }
            75% { transform: translateX(-1px); }
          }
          
          .win-animation {
            animation: celebrate 1.5s ease-in-out 3;
            border: 3px solid #10b981 !important;
            box-shadow: 0 0 15px rgba(16, 185, 129, 0.4);
          }
          
          .loss-animation {
            animation: shake 0.5s ease-in-out 3;
          }
          
          .winner-highlight {
            border: 3px solid #10b981 !important;
            background: linear-gradient(to right, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05)) !important;
            box-shadow: 0 0 15px rgba(16, 185, 129, 0.3) !important;
          }
          
          .player1-winner {
            border: 3px solid #3b82f6 !important;
            background: linear-gradient(to right, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05)) !important;
            box-shadow: 0 0 15px rgba(59, 130, 246, 0.3) !important;
          }
          
          .player2-winner {
            border: 3px solid #ef4444 !important;
            background: linear-gradient(to right, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.05)) !important;
            box-shadow: 0 0 15px rgba(239, 68, 68, 0.3) !important;
          }
        `}
      </style>
      
      {/* Top Navigation Bar */}
      <div className="flex justify-between items-center mb-6">
        <Link
          to="/1on1"
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm transition"
        >
          ← Back to Games
        </Link>
        
        <TopRightBar 
          onSwitchWallet={handleSwitchWallet}
          onWalletDisconnect={handleWalletDisconnect}
          showInTestMode={true}
        />
      </div>
      
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-center mb-8 text-gray-800">
          Tic Tac Toe Game #{gameID}
        </h1>

        {/* Game Rules - Collapsible */}
        <div className="collapsible-container">
          <button 
            className="collapsible-button"
            onClick={() => setRulesCollapsed(!rulesCollapsed)}
            style={{ 
              width: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              fontSize: '1.25rem', 
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <span>How Tic Tac Toe Works</span>
            <svg className={`w-5 h-5 transition-transform duration-200 ${rulesCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            rulesCollapsed ? 'max-h-0 opacity-0' : 'max-h-96 opacity-100 mt-3'
          }`}>
            <ul className="list-disc list-inside space-y-2 text-gray-600">
              <li>Players take turns placing <strong>X</strong> and <strong>O</strong> on a 3x3 grid</li>
              <li><strong>Player 1</strong> is always X, <strong>Player 2</strong> is always O</li>
              <li>Players alternate turns - click an empty square to make your move</li>
              <li>Win by getting <strong>three in a row</strong> horizontally, vertically, or diagonally</li>
              <li>If all squares are filled with no winner, the game ends in a <strong>draw</strong></li>
              <li>The winner takes all the money from both bets</li>
              <li>🔍 Find the contract info and verified code here: <AddressDisplayLong address={contractAddresses.TicTacToe} useExplorerLink={true} /></li>
            </ul>
          </div>
        </div>

        {/* Game Status */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700">Game Status</h2>
          {loadingStages.gameBasics ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <div className="animate-pulse bg-gray-300 h-6 w-32 rounded"></div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Your Role</p>
                <div className="animate-pulse bg-gray-300 h-6 w-24 rounded"></div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <p className="text-lg font-medium">{getStatusMessage()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Your Role</p>
                <p className="text-lg font-medium">{getUserRole()}</p>
              </div>
            </div>
          )}
        </div>

        {/* Game Board */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700">Game Board</h2>
          
          {/* Tic Tac Toe Board */}
          <div className="flex flex-col items-center">
            {renderBoard()}
            
            {/* Game Messages - Now below the game board */}
            <div className="text-center mt-4">
              {!gameState.gameFinished ? (
                <>
                  {transactionPending ? (
                    <div className="game-message-pending">
                      {transactionPending.message}
                    </div>
                  ) : (
                    gameState.currentPlayer.toLowerCase() === userState.userAddress.toLowerCase() ? (
                      <div>
                        <div className="game-message-main">Your turn</div>
                        <div className="game-message-sub">Click empty square to make your move</div>
                      </div>
                    ) : (
                      <div>
                        <div className="game-message-waiting">Opponent's turn</div>
                        <div className="game-message-sub">Waiting for opponent's move...</div>
                      </div>
                    )
                  )}
                </>
              ) : (
                getWinnerMessage() && (
                  <div className={`text-center p-4 rounded-lg ${
                    getWinnerMessage() === 'You won!' ? 'win-animation bg-green-50' :
                    getWinnerMessage() === 'You lost!' ? 'loss-animation bg-red-50' :
                    getWinnerMessage() === 'You drawed!' ? 'bg-yellow-50' : 'bg-gray-50'
                  }`}>
                    <p className={`text-2xl font-bold ${
                      getWinnerMessage() === 'You won!' ? 'text-green-600' :
                      getWinnerMessage() === 'You lost!' ? 'text-red-600' :
                      getWinnerMessage() === 'You drawed!' ? 'text-yellow-600' : 'text-gray-600'
                    }`}>{getWinnerMessage()}</p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* Players Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Player 1 */}
          <div className={`bg-white rounded-lg shadow-lg p-6 ${
            gameState.winner === gameState.player1 ? 'player1-winner' : ''
          }`}>
            <h3 className="text-xl font-semibold mb-3 text-gray-700">Player 1 (X)</h3>
            {loadingStages.gameBasics ? (
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-600">Address</p>
                  <div className="animate-pulse bg-gray-300 h-4 w-full rounded"></div>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Bet Amount</p>
                  <div className="animate-pulse bg-gray-300 h-6 w-24 rounded"></div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-600">Address</p>
                  <p className="font-mono text-xs break-all"><AddressDisplayLong address={gameState.player1} useExplorerLink={true} /></p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Bet Amount</p>
                  <p className="text-lg font-medium">{formatEth(gameState.contribution1)} POL</p>
                </div>
                {gameState.winner === gameState.player1 && (
                  <div className="text-blue-600 font-semibold">🎉 Winner!</div>
                )}
              </div>
            )}
          </div>

          {/* Player 2 */}
          <div className={`bg-white rounded-lg shadow-lg p-6 ${
            gameState.winner === gameState.player2 ? 'player2-winner' : ''
          }`}>
            <h3 className="text-xl font-semibold mb-3 text-gray-700">Player 2 (O)</h3>
            {loadingStages.gameBasics ? (
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-600">Address</p>
                  <div className="animate-pulse bg-gray-300 h-4 w-full rounded"></div>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Bet Amount</p>
                  <div className="animate-pulse bg-gray-300 h-6 w-24 rounded"></div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-600">Address</p>
                  <p className="font-mono text-xs break-all"><AddressDisplayLong address={gameState.player2} useExplorerLink={true} /></p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Bet Amount</p>
                  <p className="text-lg font-medium">{formatEth(gameState.contribution2)} POL</p>
                </div>
                {gameState.winner === gameState.player2 && (
                  <div className="text-red-600 font-semibold">🎉 Winner!</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Game Details */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-semibold mb-3 text-gray-700">Game Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <p className="text-sm text-gray-600">Game ID</p>
              <p className="font-mono">{gameID}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Pot</p>
              <p className="text-lg font-medium">
                {formatEth((parseInt(gameState.contribution1) + parseInt(gameState.contribution2)).toString())} POL
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Last Move</p>
              <p>{gameState.lastMoveTime > 0 ? new Date(gameState.lastMoveTime * 1000).toLocaleString() : 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Game Contract</p>
              <AddressDisplayLong address={contractAddresses.TicTacToe} useExplorerLink={true} />
            </div>
          </div>
          
          {/* Transaction Details - Unified Component */}
          <GameTransactionHistory
            gameId={gameID}
            gameMakerContract={gameMakerContract}
            gameContract={readOnlyContract}
            gameEvents={ticTacToeGameEvents}
            player1={gameState.player1}
            player2={gameState.player2}
          />
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4">
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default TicTacToeGame;